import React from "react";
import clsx from "clsx";
import {
	Collapse,
	Box,
	CssBaseline,
	AppBar,
	Toolbar,
	IconButton,
	Grid,
	Typography,
	FormControl,
	FormControlLabel,
	List,
	Checkbox,
	Button,
	Drawer,
	MenuItem,
	Select,
	Tooltip,
	Modal,
	Grow,
} from "@mui/material";
import { makeStyles, withStyles } from "@mui/styles";
import { useLocation } from "react-router-dom";
import emitter from "tiny-emitter/instance";
import { Link } from "react-router-dom";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import { useDispatch } from "react-redux";

import MainListItems from "./listItems";
import CalendarModal from "./screens/Dashboard/components/CalendarModal";
import FormFilters from "./components/FormFilters";
import LegendComponent from "./components/LegendComponent";
import AuthService from "./services/auth.service";
import { postUserSandbox, postDeleteRecords } from "./redux/slices/data";
import { deleteUserSandbox, restoreDataset } from "./redux/slices/sandbox";
import { getDataRows } from "./redux/slices/data";

const drawerWidth = 280;

// ADSPEND is only visible to clients whose configs include ADVERTISING.
// dataFilter is populated by Router.js (see Router.js around line 427).
const hasAdvertisingConfig = () => {
	try {
		const cfg = JSON.parse(sessionStorage.getItem("dataFilter"))?.config ?? [];
		return cfg.some((c) => Array.isArray(c?.types) && c.types.includes("ADVERTISING"));
	} catch {
		return false;
	}
};

export default function Layout(props) {
	const classes = useStyles();
	const [open, setOpen] = React.useState(true);
	const [activeRoute, setActiveRoute] = React.useState("/");
	const [periodFilter, setPeriodFilter] = React.useState("this month"); // eslint-disable-line
	// Default ON so the AG-Grid-style filter toolbar is visible without an extra click. The
	// "Filters" header button still toggles it for users who want the screen back.
	const [showFilters, setShowFilters] = React.useState(true);
	const [logoutModal, setLogoutModal] = React.useState(false);
	const [editorial, setEditorial] = React.useState(
		JSON.parse(sessionStorage.getItem("editorialChecked"))
	);
	const [advertising, setAdvertising] = React.useState(
		JSON.parse(sessionStorage.getItem("advertisingChecked"))
	);
	const [adspend, setAdspend] = React.useState(
		JSON.parse(sessionStorage.getItem("adspendChecked"))
	);
	const [selectedCompany, setSelectedCompany] = React.useState("");
	const [period, setPeriod] = React.useState(
		JSON.parse(sessionStorage.getItem("dateRange"))?.currentDataDateFrom +
			" - " +
			JSON.parse(sessionStorage.getItem("dateRange"))?.currentDataDateTo
	);

	const dispatch = useDispatch();

	const refCalendar = React.createRef();
	const mainContentRef = React.useRef(null);
	let location = useLocation();

	const LogoutModal = (
		<div
			style={{
				width: "50%",
				minHeight: 200,
				position: "absolute",
				left: "50%",
				top: "50%",
				transform: "translate(-50%, -50%)",
			}}
			className={classes.LogoutPaper}
		>
			<Box>
				<div
					style={{
						fontSize: 24,
						fontFamily: "Poppins",
						fontWeight: 500,
						color: "#263238",
						marginBottom: 10,
					}}
				>
					You will logout due to inactivity, please choose to stay
					logged in. Otherwise you will be logged out.
				</div>
			</Box>
			<Box display="flex" justifyContent="center">
				<Button
					onClick={() => {
						setLogoutModal(false);
					}}
					color="primary"
					variant="contained"
				>
					Stay logged in
				</Button>
			</Box>
		</div>
	);

	const checkForInactivity = () => {
		const expireTime = localStorage.getItem("expireTime");

		if (expireTime - 60000 < Date.now()) {
			setLogoutModal(true);
		}

		if (expireTime < Date.now()) {
			emitter.emit("logout");
		}
	};

	const updateExpireTime = () => {
		// set expireTime for 30 mins on each activity
		const expireTime = Date.now() + 1800000;
		localStorage.setItem("expireTime", expireTime);
	};

	React.useEffect(() => {
		// check activity every 30 seconds
		const interval = setInterval(() => {
			checkForInactivity();
		}, 30000);

		return () => clearInterval(interval);
	}, []);

	React.useEffect(() => {
		updateExpireTime();

		window.addEventListener("click", updateExpireTime);
		window.addEventListener("keypress", updateExpireTime);
		window.addEventListener("scroll", updateExpireTime);
		window.addEventListener("mousemove", updateExpireTime);

		return () => {
			window.addEventListener("click", updateExpireTime);
			window.addEventListener("keypress", updateExpireTime);
			window.addEventListener("scroll", updateExpireTime);
			window.addEventListener("mousemove", updateExpireTime);
		};
	}, []);

	React.useEffect(() => {
		emitter.on("refreshDateRange", (date) => {
			const from = new Date(date.startDate).toISOString().slice(0, 10);
			const to = new Date(date.endDate).toISOString().slice(0, 10);
			setPeriodFilter(from + " - " + to);
		});

		return () => emitter.off("refreshDateRange");
	}, [props.data]); //eslint-disable-line

	React.useEffect(() => {
		setActiveRoute(location.pathname);
	}, [location]);

	React.useEffect(() => {
		const company = JSON.parse(sessionStorage.getItem("selectedClient"));
		if (!company) sessionStorage.setItem("selectedCompany", 0);
		else {
			if (props.data.clients)
				setSelectedCompany(
					props.data.clients
						.map((e) => e.client.clientName)
						.indexOf(company.clientName)
				);
		}

		setPeriod(
			JSON.parse(sessionStorage.getItem("dateRange"))?.currentDataDateFrom +
				" - " +
				JSON.parse(sessionStorage.getItem("dateRange"))?.currentDataDateTo
		);
	}, [props.data]);

	const toggleEditorial = (checked) => {

		setEditorial(checked);
		sessionStorage.setItem("editorialChecked", checked);
		const values = [];
		if (checked) values.push("EDITORIAL");
		if (advertising) values.push("ADVERTISING");
		if (adspend) values.push("ADSPEND");

		if (values.length === 0) emitter.emit("filterChange");
		else {
			const ids = [];
			values.forEach((item) => {
				const index = props.data.types.map((e) => e).indexOf(item);
				ids.push(index);
			});

			const storageFilters = JSON.parse(
				sessionStorage.getItem("filters")
			);
			const filters = {
				...storageFilters,
				type: ids,
			};
			sessionStorage.setItem("filters", JSON.stringify(filters));

			emitter.emit("filter", [
				{ field: "mediaType", values: storageFilters.mediaType },
				{
					field: "brand",
					values: storageFilters.brand.map(
						(e) => props.data.brands[e]
					),
				},
				{
					field: "mediaSource",
					values: storageFilters.mediaSource.map(
						(e) => props.data.mediaSources[e].itemName
					),
				},
				{
					field: "category",
					values: storageFilters.category.map(
						(e) => props.data.categories[e]
					),
				},
				{
					field: "subCategory",
					values: storageFilters.subCategory.map(
						(e) => props.data.subcategories[e]
					),
				},
				{ field: "type", values: values },
				{
					field: "sentiment",
					values: storageFilters.sentiment.map(
						(e) => props.data.sentimet[e].itemName
					),
				},
				{
					field: "author",
					values: storageFilters.author.map(
						(e) => props.data.authors[e].itemName
					),
				},
				{
					field: "topic",
					values: storageFilters.topic.map(
						(e) => props.data.topics[e]
					),
				},
				{
					field: "advertType",
					values: storageFilters.advertType.map(
						(e) => props.data.advertTypes[e].itemName
					),
				},
				{
					field: "language",
					values: storageFilters.language.map(
						(e) => props.data.languages[e].itemName
					),
				},
				{
					field: "country",
					values: storageFilters.country.map(
						(e) => props.data.countries[e]
					),
				},
				{
					field: "region",
					values: storageFilters.location.map(
						(e) => props.data.locations[e].itemName
					),
				},
				{ field: "description", values: [storageFilters.search] },
			]);
		}
		
	};

	const toggleAdvertising = (checked) => {

		setAdvertising(checked);
		sessionStorage.setItem("advertisingChecked", checked);
		const values = [];
		if (editorial) values.push("EDITORIAL");
		if (checked) values.push("ADVERTISING");
		if (adspend) values.push("ADSPEND");

		if (values.length === 0) emitter.emit("filterChange");
		else {
			const ids = [];
			values.forEach((item) => {
				const index = props.data.types.map((e) => e).indexOf(item);
				ids.push(index);
			});

			const storageFilters = JSON.parse(
				sessionStorage.getItem("filters")
			);
			const filters = {
				...storageFilters,
				type: ids,
			};
			sessionStorage.setItem("filters", JSON.stringify(filters));

			emitter.emit("filter", [
				{ field: "mediaType", values: storageFilters.mediaType },
				{
					field: "brand",
					values: storageFilters.brand.map(
						(e) => props.data.brands[e].itemName
					),
				},
				{
					field: "mediaSource",
					values: storageFilters.mediaSource.map(
						(e) => props.data.mediaSources[e].itemName
					),
				},
				{
					field: "category",
					values: storageFilters.category.map(
						(e) => props.data.categories[e].itemName
					),
				},
				{
					field: "subCategory",
					values: storageFilters.subCategory.map(
						(e) => props.data.subcategories[e].itemName
					),
				},
				{ field: "type", values: values },
				{
					field: "sentiment",
					values: storageFilters.sentiment.map(
						(e) => props.data.sentimet[e].itemName
					),
				},
				{
					field: "author",
					values: storageFilters.author.map(
						(e) => props.data.authors[e].itemName
					),
				},
				{
					field: "topic",
					values: storageFilters.topic.map(
						(e) => props.data.topics[e].itemName
					),
				},
				{
					field: "advertType",
					values: storageFilters.advertType.map(
						(e) => props.data.advertTypes[e].itemName
					),
				},
				{
					field: "language",
					values: storageFilters.language.map(
						(e) => props.data.languages[e].itemName
					),
				},
				{
					field: "country",
					values: storageFilters.country.map(
						(e) => props.data.countries[e].itemName
					),
				},
				{
					field: "region",
					values: storageFilters.location.map(
						(e) => props.data.locations[e].itemName
					),
				},
				{ field: "description", values: [storageFilters.search] },
			]);
		}

	};

	const toggleAdspend = (checked) => {

		setAdspend(checked);
		sessionStorage.setItem("adspendChecked", checked);
		const values = [];
		if (editorial) values.push("EDITORIAL");
		if (advertising) values.push("ADVERTISING");
		if (checked) values.push("ADSPEND");

		if (values.length === 0) emitter.emit("filterChange");
		else {
			const ids = [];
			values.forEach((item) => {
				const index = props.data.types.map((e) => e).indexOf(item);
				ids.push(index);
			});

			const storageFilters = JSON.parse(
				sessionStorage.getItem("filters")
			);
			const filters = {
				...storageFilters,
				type: ids,
			};
			sessionStorage.setItem("filters", JSON.stringify(filters));

			emitter.emit("filter", [
				{ field: "mediaType", values: storageFilters.mediaType },
				{
					field: "brand",
					values: storageFilters.brand.map(
						(e) => props.data.brands[e].itemName
					),
				},
				{
					field: "mediaSource",
					values: storageFilters.mediaSource.map(
						(e) => props.data.mediaSources[e].itemName
					),
				},
				{
					field: "category",
					values: storageFilters.category.map(
						(e) => props.data.categories[e].itemName
					),
				},
				{
					field: "subCategory",
					values: storageFilters.subCategory.map(
						(e) => props.data.subcategories[e].itemName
					),
				},
				{ field: "type", values: values },
				{
					field: "sentiment",
					values: storageFilters.sentiment.map(
						(e) => props.data.sentimet[e].itemName
					),
				},
				{
					field: "author",
					values: storageFilters.author.map(
						(e) => props.data.authors[e].itemName
					),
				},
				{
					field: "topic",
					values: storageFilters.topic.map(
						(e) => props.data.topics[e].itemName
					),
				},
				{
					field: "advertType",
					values: storageFilters.advertType.map(
						(e) => props.data.advertTypes[e].itemName
					),
				},
				{
					field: "language",
					values: storageFilters.language.map(
						(e) => props.data.languages[e].itemName
					),
				},
				{
					field: "country",
					values: storageFilters.country.map(
						(e) => props.data.countries[e].itemName
					),
				},
				{
					field: "region",
					values: storageFilters.location.map(
						(e) => props.data.locations[e].itemName
					),
				},
				{ field: "description", values: [storageFilters.search] },
			]);
		}

	};

	const handleDrawerClose = () => {
		setOpen(false);
	};
	const handleDrawerToggle = (e) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}
		setOpen(prev => {
			const newState = !prev;
			console.log('Drawer toggle - previous:', prev, 'new:', newState);
			return newState;
		});
	};

	const sandboxItems = () => {
		dispatch(postUserSandbox());
		dispatch(getDataRows());
		emitter.emit("getData");
	};

	const restoreSandbox = () =>{
		dispatch(deleteUserSandbox());
		dispatch(getDataRows());
		emitter.emit("getData");
	};

	const restoreAnalytics = () => {
		dispatch(restoreDataset());
		emitter.emit("restoreAnalytics");
	};

	const deleteSelectedRecords = () => {
		dispatch(postDeleteRecords());
		emitter.emit("getData");
	}

	// On /data the DataGrid manages its own scroll, so the page (<main>) must NOT scroll —
	// otherwise we get two vertical scrollbars. Turn <main> into a flex column that fills the
	// viewport, keep the chrome (spacer/filters/legend) at natural height, and let the grid
	// subtree consume the remaining space. This replaces the old `calc(100vh - 420px)` magic
	// number in Table.js, which produced the double scrollbar whenever the real chrome height
	// (e.g. the always-on filter toolbar) differed from that estimate.
	const isDataRoute = activeRoute === "/data";

	return (
        <div className={classes.root}>
            <Modal
				open={logoutModal}
				onClose={() => setLogoutModal(false)}
				aria-labelledby="modal-logout"
				aria-describedby="modal-logout"
			>
				<Grow in={logoutModal}>{LogoutModal}</Grow>
			</Modal>
            <CssBaseline />
            <CalendarModal ref={refCalendar} />
            <AppBar
				position="fixed"
				className={clsx(classes.appBar, open && classes.appBarShift)}
				sx={{
					backgroundColor: "#ffffff",
					"--AppBar-background": "#ffffff",
					"&.MuiAppBar-root": {
						backgroundColor: "#ffffff",
						"--AppBar-background": "#ffffff",
					},
				}}
			>
				<Toolbar className={classes.toolbar}>
					<IconButton
						edge="start"
						color="inherit"
						aria-label={open ? "close drawer" : "open drawer"}
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							handleDrawerToggle(e);
						}}
						className={classes.menuButton}
						style={{ 
							display: open ? 'none' : 'inline-flex',
							pointerEvents: open ? 'none' : 'auto',
							zIndex: 1300,
							position: 'relative'
						}}
						size="large">
						<MenuIcon />
					</IconButton>
					<Grid container direction="row" alignItems="center" style={{ width: "100%", flexWrap: "nowrap" }}>
						<Grid item>
							<Typography
								component="h1"
								variant="h6"
								color="inherit"
								noWrap
								className={classes.title}
							>
								{activeRoute === "/wordCloud"
									? "Word Cloud"
									: activeRoute === "/advertType"
										? "Advertising Type"
										: activeRoute === "/faq"
											? "Frequently Asked Questions"
											: activeRoute.replace("/", "")}
							</Typography>
						</Grid>

						{activeRoute === "/userManagement" ||
							activeRoute === "/clientManagement" ||
							activeRoute === "/alertHistory" ||
							activeRoute === "/faq" ? null : (
							<Grid item>
								<FormControl className={classes.periodFilter}>
									<Button
										onClick={() => {
											refCalendar?.current.toggleModal(
												true
											);
										}}
										sx={{
											borderRadius: "8px",
											padding: "8px 14px",
											transition: "all 0.2s ease",
											"&:hover": {
												backgroundColor: "#F7F9FC",
											},
										}}
									>
										<Typography
											style={{
												fontSize: 15,
												fontFamily: "Poppins-Medium",
												fontWeight: 500,
												color: "#3966D0",
												textTransform: "none",
												marginRight: 8,
												letterSpacing: "-0.01em",
											}}
										>
											{period}
										</Typography>
										<svg
											width="12"
											height="8"
											viewBox="0 0 12 8"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M5.88477 7.16211C6.19531 7.15625 6.44727 7.04492 6.69336 6.79297L11 2.38086C11.1875 2.19336 11.2754 1.97656 11.2754 1.70703C11.2754 1.16797 10.8359 0.722656 10.3027 0.722656C10.0391 0.722656 9.78711 0.833984 9.58203 1.03906L5.89062 4.86523L2.1875 1.03906C1.98828 0.839844 1.73633 0.722656 1.4668 0.722656C0.933594 0.722656 0.494141 1.16797 0.494141 1.70703C0.494141 1.9707 0.587891 2.19336 0.769531 2.38672L5.07617 6.79297C5.33398 7.04492 5.58008 7.16211 5.88477 7.16211Z"
												fill="#3966D0"
											/>
										</svg>
									</Button>
								</FormControl>
							</Grid>
						)}

						{activeRoute === "/overview" ? (
							<>
								<Button
										onClick={() =>
											setShowFilters(!showFilters)
										}
										sx={{
											borderRadius: "8px",
											padding: "8px 14px",
											marginLeft: "12px",
											transition: "all 0.2s ease",
											"&:hover": {
												backgroundColor: showFilters ? "#EBF0FA" : "#F7F9FC",
											},
											backgroundColor: showFilters ? "#EBF0FA" : "transparent",
										}}
									>
												<Box
													display="flex"
													flexDirection="row"
													alignItems="center"
												>
													{showFilters ? (
														<>
															<svg
																width="20"
																height="20"
																viewBox="0 0 22 22"
																fill="none"
																xmlns="http://www.w3.org/2000/svg"
															>
																<path
																	d="M11.2944 21.0557C17.0161 21.0557 21.7534 16.3184 21.7534 10.5967C21.7534 4.88525 17.0059 0.137695 11.2842 0.137695C5.57275 0.137695 0.835449 4.88525 0.835449 10.5967C0.835449 16.3184 5.58301 21.0557 11.2944 21.0557ZM11.2944 19.3125C6.45459 19.3125 2.58887 15.4365 2.58887 10.5967C2.58887 5.76709 6.44434 1.88086 11.2842 1.88086C16.124 1.88086 20 5.76709 20.0103 10.5967C20.0205 15.4365 16.1343 19.3125 11.2944 19.3125ZM5.70605 8.62793H16.8931C17.3442 8.62793 17.6724 8.34082 17.6724 7.91016C17.6724 7.46924 17.3442 7.18213 16.8931 7.18213H5.70605C5.25488 7.18213 4.93701 7.46924 4.93701 7.91016C4.93701 8.34082 5.25488 8.62793 5.70605 8.62793ZM7.23389 12.022H15.3755C15.8267 12.022 16.1445 11.7349 16.1445 11.3042C16.1445 10.8633 15.8267 10.5762 15.3755 10.5762H7.23389C6.77246 10.5762 6.45459 10.8633 6.45459 11.3042C6.45459 11.7349 6.77246 12.022 7.23389 12.022ZM8.81299 15.4263H13.7861C14.2373 15.4263 14.5654 15.1392 14.5654 14.7085C14.5654 14.2676 14.2373 13.9805 13.7861 13.9805H8.81299C8.36182 13.9805 8.04395 14.2676 8.04395 14.7085C8.04395 15.1392 8.36182 15.4263 8.81299 15.4263Z"
																	fill="#1A202C"
																/>
															</svg>
															<Typography
																style={{
																	fontSize: 15,
																	fontFamily:
																		"Poppins-Medium",
																	fontWeight: 500,
																	color: "#1A202C",
																	textTransform:
																		"none",
																	marginLeft: 8,
																	letterSpacing: "-0.01em",
																}}
															>
																Filter report
															</Typography>
														</>
													) : (
														<>
															<svg
																width="20"
																height="20"
																viewBox="0 0 22 22"
																fill="none"
																xmlns="http://www.w3.org/2000/svg"
															>
																<path
																	d="M11.2944 21.0557C17.0161 21.0557 21.7534 16.3184 21.7534 10.5967C21.7534 4.88525 17.0059 0.137695 11.2842 0.137695C5.57275 0.137695 0.835449 4.88525 0.835449 10.5967C0.835449 16.3184 5.58301 21.0557 11.2944 21.0557ZM11.2944 19.3125C6.45459 19.3125 2.58887 15.4365 2.58887 10.5967C2.58887 5.76709 6.44434 1.88086 11.2842 1.88086C16.124 1.88086 20 5.76709 20.0103 10.5967C20.0205 15.4365 16.1343 19.3125 11.2944 19.3125ZM5.70605 8.62793H16.8931C17.3442 8.62793 17.6724 8.34082 17.6724 7.91016C17.6724 7.46924 17.3442 7.18213 16.8931 7.18213H5.70605C5.25488 7.18213 4.93701 7.46924 4.93701 7.91016C4.93701 8.34082 5.25488 8.62793 5.70605 8.62793ZM7.23389 12.022H15.3755C15.8267 12.022 16.1445 11.7349 16.1445 11.3042C16.1445 10.8633 15.8267 10.5762 15.3755 10.5762H7.23389C6.77246 10.5762 6.45459 10.8633 6.45459 11.3042C6.45459 11.7349 6.77246 12.022 7.23389 12.022ZM8.81299 15.4263H13.7861C14.2373 15.4263 14.5654 15.1392 14.5654 14.7085C14.5654 14.2676 14.2373 13.9805 13.7861 13.9805H8.81299C8.36182 13.9805 8.04395 14.2676 8.04395 14.7085C8.04395 15.1392 8.36182 15.4263 8.81299 15.4263Z"
																	fill="#3966D0"
																/>
															</svg>
															<Typography
																style={{
																	fontSize: 15,
																	fontFamily:
																		"Poppins-Medium",
																	fontWeight: 500,
																	color: "#3966D0",
																	textTransform:
																		"none",
																	marginLeft: 8,
																	letterSpacing: "-0.01em",
																}}
															>
																Filter report
															</Typography>
														</>
													)}
												</Box>
											</Button>
								

								<FormControlLabel
									className={classes.label}
									style={{
										paddingRight: 16,
										marginLeft: 16,
										borderRadius: 8,
										padding: "6px 12px 6px 8px",
										transition: "all 0.2s ease",
										backgroundColor: editorial ? "#EBF0FA" : "transparent",
									}}
									control={
										<Checkbox
											value="editorial"
											checked={editorial}
											sx={{
												color: editorial ? "#3966D0" : "#718096",
												padding: "6px",
												"&.Mui-checked": {
													color: "#3966D0",
												},
											}}
											onChange={() =>
												toggleEditorial(!editorial)
											}
										/>
									}
									label="Editorial"
								/>
								<FormControlLabel
									className={classes.label}
									style={{
										borderRadius: 8,
										padding: "6px 12px 6px 8px",
										transition: "all 0.2s ease",
										backgroundColor: advertising ? "#EBF0FA" : "transparent",
									}}
									control={
										<Checkbox
											value="advertising"
											checked={advertising}
											sx={{
												color: advertising ? "#3966D0" : "#718096",
												padding: "6px",
												"&.Mui-checked": {
													color: "#3966D0",
												},
											}}
											onChange={() =>
												toggleAdvertising(!advertising)
											}
										/>
									}
									label="Advertising"
								/>
								{hasAdvertisingConfig() && (
									<FormControlLabel
										className={classes.label}
										style={{
											borderRadius: 8,
											padding: "6px 12px 6px 8px",
											transition: "all 0.2s ease",
											backgroundColor: adspend ? "#EBF0FA" : "transparent",
										}}
										control={
											<Checkbox
												value="adspend"
												checked={!!adspend}
												sx={{
													color: adspend ? "#3966D0" : "#718096",
													padding: "6px",
													"&.Mui-checked": {
														color: "#3966D0",
													},
												}}
												onChange={() =>
													toggleAdspend(!adspend)
												}
											/>
										}
										label="Adspend"
									/>
								)}
							</>
						) : (
							<>
								{![
									"/sandbox",
									"/alertHistory",
									"/faq",
									"/userManagement",
									"/clientManagement",
								].includes(activeRoute) && (
										<>
											<Button
												onClick={() =>
													setShowFilters(!showFilters)
												}
												sx={{
													borderRadius: "8px",
													padding: "8px 14px",
													marginLeft: "12px",
													transition: "all 0.2s ease",
													"&:hover": {
														backgroundColor: showFilters ? "#EBF0FA" : "#F7F9FC",
													},
													backgroundColor: showFilters ? "#EBF0FA" : "transparent",
												}}
											>
												<Box
													display="flex"
													flexDirection="row"
													alignItems="center"
												>
													{showFilters ? (
														<>
															<svg
																width="20"
																height="20"
																viewBox="0 0 22 22"
																fill="none"
																xmlns="http://www.w3.org/2000/svg"
															>
																<path
																	d="M11.2944 21.0557C17.0161 21.0557 21.7534 16.3184 21.7534 10.5967C21.7534 4.88525 17.0059 0.137695 11.2842 0.137695C5.57275 0.137695 0.835449 4.88525 0.835449 10.5967C0.835449 16.3184 5.58301 21.0557 11.2944 21.0557ZM11.2944 19.3125C6.45459 19.3125 2.58887 15.4365 2.58887 10.5967C2.58887 5.76709 6.44434 1.88086 11.2842 1.88086C16.124 1.88086 20 5.76709 20.0103 10.5967C20.0205 15.4365 16.1343 19.3125 11.2944 19.3125ZM5.70605 8.62793H16.8931C17.3442 8.62793 17.6724 8.34082 17.6724 7.91016C17.6724 7.46924 17.3442 7.18213 16.8931 7.18213H5.70605C5.25488 7.18213 4.93701 7.46924 4.93701 7.91016C4.93701 8.34082 5.25488 8.62793 5.70605 8.62793ZM7.23389 12.022H15.3755C15.8267 12.022 16.1445 11.7349 16.1445 11.3042C16.1445 10.8633 15.8267 10.5762 15.3755 10.5762H7.23389C6.77246 10.5762 6.45459 10.8633 6.45459 11.3042C6.45459 11.7349 6.77246 12.022 7.23389 12.022ZM8.81299 15.4263H13.7861C14.2373 15.4263 14.5654 15.1392 14.5654 14.7085C14.5654 14.2676 14.2373 13.9805 13.7861 13.9805H8.81299C8.36182 13.9805 8.04395 14.2676 8.04395 14.7085C8.04395 15.1392 8.36182 15.4263 8.81299 15.4263Z"
																	fill="#1A202C"
																/>
															</svg>
															<Typography
																style={{
																	fontSize: 15,
																	fontFamily:
																		"Poppins-Medium",
																	fontWeight: 500,
																	color: "#1A202C",
																	textTransform:
																		"none",
																	marginLeft: 8,
																	letterSpacing: "-0.01em",
																}}
															>
																Filter report
															</Typography>
														</>
													) : (
														<>
															<svg
																width="20"
																height="20"
																viewBox="0 0 22 22"
																fill="none"
																xmlns="http://www.w3.org/2000/svg"
															>
																<path
																	d="M11.2944 21.0557C17.0161 21.0557 21.7534 16.3184 21.7534 10.5967C21.7534 4.88525 17.0059 0.137695 11.2842 0.137695C5.57275 0.137695 0.835449 4.88525 0.835449 10.5967C0.835449 16.3184 5.58301 21.0557 11.2944 21.0557ZM11.2944 19.3125C6.45459 19.3125 2.58887 15.4365 2.58887 10.5967C2.58887 5.76709 6.44434 1.88086 11.2842 1.88086C16.124 1.88086 20 5.76709 20.0103 10.5967C20.0205 15.4365 16.1343 19.3125 11.2944 19.3125ZM5.70605 8.62793H16.8931C17.3442 8.62793 17.6724 8.34082 17.6724 7.91016C17.6724 7.46924 17.3442 7.18213 16.8931 7.18213H5.70605C5.25488 7.18213 4.93701 7.46924 4.93701 7.91016C4.93701 8.34082 5.25488 8.62793 5.70605 8.62793ZM7.23389 12.022H15.3755C15.8267 12.022 16.1445 11.7349 16.1445 11.3042C16.1445 10.8633 15.8267 10.5762 15.3755 10.5762H7.23389C6.77246 10.5762 6.45459 10.8633 6.45459 11.3042C6.45459 11.7349 6.77246 12.022 7.23389 12.022ZM8.81299 15.4263H13.7861C14.2373 15.4263 14.5654 15.1392 14.5654 14.7085C14.5654 14.2676 14.2373 13.9805 13.7861 13.9805H8.81299C8.36182 13.9805 8.04395 14.2676 8.04395 14.7085C8.04395 15.1392 8.36182 15.4263 8.81299 15.4263Z"
																	fill="#3966D0"
																/>
															</svg>
															<Typography
																style={{
																	fontSize: 15,
																	fontFamily:
																		"Poppins-Medium",
																	fontWeight: 500,
																	color: "#3966D0",
																	textTransform:
																		"none",
																	marginLeft: 8,
																	letterSpacing: "-0.01em",
																}}
															>
																Filter report
															</Typography>
														</>
													)}
												</Box>
											</Button>
										</>
									)}

								{activeRoute === "/data" && (
									<Box
										display="flex"
										flexDirection="row"
										alignItems="center"
										style={{ marginLeft: 10 }}
									>
										<Tooltip
											title="Remove selection from Analytics"
											placement="bottom"
										>
											<IconButton 
												color="inherit" 
												onClick={() => sandboxItems()} 
												size="large"
												sx={{
													padding: "10px",
													borderRadius: "8px",
													transition: "all 0.2s ease",
													"&:hover": {
														backgroundColor: "#FEF3C7",
													},
												}}
											>
												<svg
													width="22"
													height="22"
													viewBox="0 0 24 24"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V12"
														stroke="#F59E0B"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M21 12H3"
														stroke="#F59E0B"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M12 3V12"
														stroke="#F59E0B"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M8 7L12 3L16 7"
														stroke="#F59E0B"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											</IconButton>
										</Tooltip>
										<Tooltip
											title="Restore dataset"
											placement="bottom"
										>
											<IconButton 
												color="inherit" 
												onClick={restoreAnalytics} 
												size="large"
												sx={{
													padding: "10px",
													borderRadius: "8px",
													transition: "all 0.2s ease",
													"&:hover": {
														backgroundColor: "#F7F9FC",
													},
												}}
											>
												<svg
													width="22"
													height="22"
													viewBox="0 0 24 24"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M1 4V10H7"
														stroke="#3966D0"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M23 20V14H17"
														stroke="#3966D0"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M20.49 9C19.9828 7.56678 19.1209 6.28536 17.9845 5.27542C16.8482 4.26548 15.4745 3.55976 13.9917 3.22426C12.5089 2.88877 10.9652 2.93434 9.50481 3.35677C8.04437 3.77921 6.71475 4.56471 5.64 5.64L1 10M23 14L18.36 18.36C17.2853 19.4353 15.9556 20.2208 14.4952 20.6432C13.0348 21.0657 11.4911 21.1112 10.0083 20.7757C8.52547 20.4402 7.1518 19.7345 6.01547 18.7246C4.87913 17.7146 4.01717 16.4332 3.51 15"
														stroke="#3966D0"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											</IconButton>
										</Tooltip>
										<Tooltip
											title="Export to Excel"
											placement="bottom"
										>
											<IconButton
                                                color="inherit"
                                                onClick={() => {
													emitter.emit("export");
												}}
                                                size="large"
												sx={{
													padding: "10px",
													borderRadius: "8px",
													transition: "all 0.2s ease",
													"&:hover": {
														backgroundColor: "#F7F9FC",
													},
												}}
											>
												<svg
													width="22"
													height="22"
													viewBox="0 0 24 24"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
														stroke="#10B981"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M14 2V8H20"
														stroke="#10B981"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M8 13H16"
														stroke="#10B981"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M8 17H16"
														stroke="#10B981"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M8 9H10"
														stroke="#10B981"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											</IconButton>
										</Tooltip>
										{props.user?.role === "Admin" &&
											props.user?.internal ? (
										<Tooltip
											title="Delete Selected Records"
											placement="bottom"
										>
											<IconButton
                                                color="inherit"
                                                onClick={() => {
													if (window.confirm('Are you sure you want to delete the selected records?')) {
														deleteSelectedRecords();
													}
												}}
                                                size="large"
												sx={{
													padding: "10px",
													borderRadius: "8px",
													transition: "all 0.2s ease",
													"&:hover": {
														backgroundColor: "#FEE2E2",
													},
												}}
											>
												<svg
													width="22"
													height="22"
													viewBox="0 0 24 24"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M3 6H5H21"
														stroke="#DC2626"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
														stroke="#DC2626"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M10 11V17"
														stroke="#DC2626"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M14 11V17"
														stroke="#DC2626"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											</IconButton>
										</Tooltip>
										) : null}
									</Box>
								)}

								{activeRoute === "/sandbox" && (
									<Box
										display="flex"
										flexDirection="row"
										alignItems="center"
									>
										<Tooltip
											title="Restore dataset"
											placement="bottom"
										>
											<IconButton 
												color="inherit" 
												onClick={restoreSandbox} 
												size="large"
												sx={{
													padding: "10px",
													borderRadius: "8px",
													transition: "all 0.2s ease",
													"&:hover": {
														backgroundColor: "#F7F9FC",
													},
												}}
											>
												<svg
													width="22"
													height="22"
													viewBox="0 0 24 24"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M1 4V10H7"
														stroke="#3966D0"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M23 20V14H17"
														stroke="#3966D0"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M20.49 9C19.9828 7.56678 19.1209 6.28536 17.9845 5.27542C16.8482 4.26548 15.4745 3.55976 13.9917 3.22426C12.5089 2.88877 10.9652 2.93434 9.50481 3.35677C8.04437 3.77921 6.71475 4.56471 5.64 5.64L1 10M23 14L18.36 18.36C17.2853 19.4353 15.9556 20.2208 14.4952 20.6432C13.0348 21.0657 11.4911 21.1112 10.0083 20.7757C8.52547 20.4402 7.1518 19.7345 6.01547 18.7246C4.87913 17.7146 4.01717 16.4332 3.51 15"
														stroke="#3966D0"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											</IconButton>
										</Tooltip>
										<Tooltip
											title="Export to Excel"
											placement="bottom"
										>
											<IconButton
                                                color="inherit"
                                                onClick={() => {
													emitter.emit("export");
												}}
                                                size="large"
												sx={{
													padding: "10px",
													borderRadius: "8px",
													transition: "all 0.2s ease",
													"&:hover": {
														backgroundColor: "#F7F9FC",
													},
												}}
											>
												<svg
													width="22"
													height="22"
													viewBox="0 0 24 24"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
														stroke="#10B981"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M14 2V8H20"
														stroke="#10B981"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M8 13H16"
														stroke="#10B981"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M8 17H16"
														stroke="#10B981"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
													<path
														d="M8 9H10"
														stroke="#10B981"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											</IconButton>
										</Tooltip>
									</Box>
								)}
							</>
						)}

						<Grid item style={{ flex: 1 }}>
							<Grid container justifyContent="flex-end">
								<Grid item>
									<CustomSelect
										value={selectedCompany}
										classes={{ root: classes.select }}
										style={{ marginRight: 20 }}
										sx={{
											'&:before': { display: 'none' },
											'&:after': { display: 'none' },
											fontSize: 14,
											fontFamily: "Poppins-Medium",
											color: "#1A202C",
											borderRadius: "8px",
											padding: "6px 12px",
											transition: "all 0.2s ease",
											"&:hover": {
												backgroundColor: "#F7F9FC",
											},
										}}
										inputProps={{
											"aria-label": "Without label",
										}}
										onChange={(event) => {
											setSelectedCompany(
												event.target.value
											);
											sessionStorage.setItem(
												"selectedClient",
												JSON.stringify(
													props.data.clients[
														event.target.value
													].client
												)
											);
											sessionStorage.setItem(
												"mediaTypes",
												JSON.stringify([])
											);
											emitter.emit("getData");
										}}
										MenuProps={{
											anchorOrigin: {
												vertical: "bottom",
												horizontal: "left",
											},
											transformOrigin: {
												vertical: "top",
												horizontal: "left",
											},
											getContentAnchorEl: null,
										}}
										IconComponent={() => (
											<svg
												width="12"
												height="8"
												viewBox="0 0 12 8"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													d="M1.41 -7.62939e-08L6 4.59L10.59 -7.62939e-08L12 1.42L6 7.42L0 1.42L1.41 -7.62939e-08Z"
													fill="#909597"
												/>
											</svg>
										)}
									>
										{props.data?.clients &&
											props.data.clients.map(
												(item, index) => {
													return (
														<MenuItem
															key={
																"client" +
																index.toString()
															}
															className={
																classes.menuItem
															}
															style={{
																fontSize: 13,
																fontFamily:
																	"Poppins-Medium",
																color: "#263238",
															}}
															value={index}
														>
															<div
																className={
																	classes.selectItem
																}
															>
																{
																	item.client
																		.clientName
																}
															</div>
														</MenuItem>
													);
												}
											)}
									</CustomSelect>
								</Grid>

								<Grid item>
									<CustomSelect
										value={0}
										classes={{ root: classes.select }}
										sx={{
											'&:before': { display: 'none' },
											'&:after': { display: 'none' },
										}}
										inputProps={{
											"aria-label": "Without label",
										}}
										MenuProps={{
											anchorOrigin: {
												vertical: "bottom",
												horizontal: "left",
											},
											transformOrigin: {
												vertical: "top",
												horizontal: "left",
											},
											getContentAnchorEl: null,
										}}
										IconComponent={() => (
											<svg
												width="12"
												height="8"
												viewBox="0 0 12 8"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													d="M1.41 -7.62939e-08L6 4.59L10.59 -7.62939e-08L12 1.42L6 7.42L0 1.42L1.41 -7.62939e-08Z"
													fill="#909597"
												/>
											</svg>
										)}
									>
										<MenuItem
											hidden
											className={classes.menuItem}
											value={0}
										>
											<div
												className={classes.selectItem}
												onClick={() => { }}
											>
												{
													AuthService.getCurrentUser()?.firstName
												}{" "}
												{
													AuthService.getCurrentUser()?.lastName
												}
											</div>
										</MenuItem>
										<MenuItem
											selected={
												props.activeRoute === "/faq"
											}
											component={(props) => (
												<Link {...props} to={"/faq"} />
											)}
											classes={{
												selected: classes.selected,
											}}
										>
											<ListItemText
												className={
													props.activeRoute === "/faq"
														? classes.textSelected
														: classes.text
												}
												primary="Help Center - FAQ"
											/>
										</MenuItem>
										{props.user?.role === "Admin" &&
											props.user?.internal ? (
											<MenuItem
												selected={
													props.activeRoute ===
													"/userManagement"
												}
												component={(props) => (
													<Link
														{...props}
														to={"/userManagement"}
													/>
												)}
												classes={{
													selected: classes.selected,
												}}
											>
												<ListItemText
													className={
														props.activeRoute ===
															"/userManagement"
															? classes.textSelected
															: classes.text
													}
													primary="User Management"
												/>
											</MenuItem>
										) : null}
										{props.user?.role === "Admin" &&
											props.user?.internal ? (
											<MenuItem
												selected={
													props.activeRoute ===
													"/clientManagement"
												}
												component={(props) => (
													<Link
														{...props}
														to={"/clientManagement"}
													/>
												)}
												classes={{
													selected: classes.selected,
												}}
											>
												<ListItemText
													className={
														props.activeRoute ===
															"/clientManagement"
															? classes.textSelected
															: classes.text
													}
													primary="Client Management"
												/>
											</MenuItem>
										) : null}
										<MenuItem
											className={classes.menuItem}
											value={2}
										>
											<div
												className={classes.selectItem}
												style={{ color: "#D26767" }}
												onClick={() => {
													emitter.emit("logout");
												}}
											>
												Logout
											</div>
										</MenuItem>
									</CustomSelect>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Toolbar>
			</AppBar>
			<Drawer
				variant="permanent"
				key={`drawer-${open}`}
				classes={{
					paper: clsx(
						classes.drawerPaper,
						!open && classes.drawerPaperClose
					),
				}}
				open={open}
			>
				<div className={classes.toolbarIcon}>
					<img
						src={require("./assets/img/logo-01.png")}
						alt="logo"
						style={{ width: 140, height: "auto" }}
					/>
					<IconButton 
						onClick={handleDrawerClose} 
						size="small"
						sx={{
							padding: "8px",
							borderRadius: "6px",
							transition: "all 0.2s ease",
							"&:hover": {
								backgroundColor: "#E8EBED",
							},
						}}
					>
						<svg
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z"
								fill="#4A5568"
							/>
						</svg>
					</IconButton>
				</div>
				<List sx={{ padding: "16px 12px" }}>
					<MainListItems
						activeRoute={activeRoute}
						user={props.user}
					/>
				</List>
			</Drawer>
            <main
				ref={mainContentRef}
				className={clsx(classes.content, open && classes.contentShift)}
				style={isDataRoute ? { display: 'flex', flexDirection: 'column', overflow: 'hidden' } : undefined}
			>
				<div className={classes.appBarSpacer} style={isDataRoute ? { flexShrink: 0 } : undefined} />

				<div style={{ position: 'relative', overflow: 'hidden', ...(isDataRoute ? { flexShrink: 0 } : {}) }}>
					<Collapse
						in={showFilters}
						timeout={300}
						unmountOnExit={false}
						onEnter={(node) => {
							// Prevent scrollTop access on null
							if (!node || !node.parentElement) return;
						}}
					>
						<div>
							{props.data && (
								<FormFilters
									data={props.data}
									onClose={() => setShowFilters(false)}
								/>
							)}
						</div>
					</Collapse>
				</div>

				{![
					"/overview",
					"/wordCloud",
					"/sandbox",
					"/faq",
					"/userManagement",
					"/clientManagement",
					"/alertHistory",
				].includes(activeRoute) ? (
					isDataRoute ? (
						<div style={{ flexShrink: 0 }}>
							<LegendComponent route={location} data={props.data} />
						</div>
					) : (
						<LegendComponent route={location} data={props.data} />
					)
				) : null}
				{isDataRoute ? (
					<div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
						{props.children}
					</div>
				) : (
					props.children
				)}
			</main>
        </div>
    );
}

const CustomSelect = withStyles({
	root: {
		paddingRight: 25,
	},
})(Select);

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
	},
	toolbar: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-start",
		paddingRight: 32,
		paddingLeft: 24,
		minHeight: 72,
		...theme.mixins.toolbar,
	},
	toolbarIcon: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "0 20px 0 24px",
		minHeight: 72,
		borderBottom: "1px solid #E8EBED",
		...theme.mixins.toolbar,
	},
	appBar: {
		zIndex: theme.zIndex.drawer + 1,
		marginLeft: theme.spacing(7),
		width: `calc(100% - ${theme.spacing(7)}px) !important`,
		maxWidth: `calc(100% - ${theme.spacing(7)}px) !important`,
		transition: theme.transitions.create(["width", "margin"], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		backgroundColor: "#ffffff !important",
		"--AppBar-background": "#ffffff",
		boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08)",
		borderBottom: "1px solid #E8EBED",
		"&.MuiAppBar-root": {
			backgroundColor: "#ffffff !important",
			"--AppBar-background": "#ffffff",
			width: `calc(100% - ${theme.spacing(7)}px) !important`,
			maxWidth: `calc(100% - ${theme.spacing(7)}px) !important`,
		},
		[theme.breakpoints.up("sm")]: {
			marginLeft: theme.spacing(9),
			width: `calc(100% - ${theme.spacing(9)}px) !important`,
			maxWidth: `calc(100% - ${theme.spacing(9)}px) !important`,
			"&.MuiAppBar-root": {
				width: `calc(100% - ${theme.spacing(9)}px) !important`,
				maxWidth: `calc(100% - ${theme.spacing(9)}px) !important`,
			},
		},
	},
	appBarShift: {
		marginLeft: drawerWidth,
		width: `calc(100% - ${drawerWidth}px) !important`,
		maxWidth: `calc(100% - ${drawerWidth}px) !important`,
		transition: theme.transitions.create(["width", "margin"], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
		"&.MuiAppBar-root": {
			width: `calc(100% - ${drawerWidth}px) !important`,
			maxWidth: `calc(100% - ${drawerWidth}px) !important`,
		},
	},
	menuButton: {
		marginRight: 20,
		padding: 12,
		borderRadius: 8,
		transition: "all 0.2s ease",
		"&:hover": {
			backgroundColor: "#F7F9FC",
		},
	},
	menuButtonHidden: {
		display: "none",
	},
	title: {
		// flexGrow: 1,
		fontSize: 22,
		fontFamily: "Poppins-SemiBold",
		color: "#1A202C",
		marginRight: 40,
		textTransform: "capitalize",
		fontWeight: 600,
		letterSpacing: "-0.02em",
	},
	periodFilter: {
		marginRight: 24,
		"& .MuiButton-root": {
			borderRadius: 8,
			padding: "8px 16px",
			transition: "all 0.2s ease",
			"&:hover": {
				backgroundColor: "#F7F9FC",
			},
		},
	},
	select: {
		paddingRight: "10px!important",
	},
	selectItem: {
		fontSize: 14,
		fontFamily: "Poppins-Medium",
		fontWeight: 500,
		color: "#1A202C",
		letterSpacing: "-0.01em",
		"&:hover": {
			color: "#3966D0",
		},
	},
	menuItem: {
		fontSize: 14,
		fontFamily: "Poppins-Medium",
		fontWeight: 500,
		color: "#4A5568",
		padding: "10px 16px",
		borderRadius: "6px",
		margin: "2px 8px",
		letterSpacing: "-0.01em",
		"&:hover": {
			backgroundColor: "#F7F9FC!important",
			color: "#1A202C",
		},
	},
	label: {
		fontSize: 14,
		fontFamily: "Poppins",
		color: "#4A5568",
		fontWeight: 500,
		lineHeight: 20,
		letterSpacing: "-0.01em",
		marginLeft: 8,
	},
	checkbox: {
		padding: 0,
		marginRight: 3,
	},
	drawerPaper: {
		position: "relative",
		whiteSpace: "nowrap",
		width: drawerWidth,
		transition: theme.transitions.create("width", {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
		backgroundColor: "#FAFBFC",
		borderRight: "1px solid #E8EBED",
		boxShadow: "none",
	},
	drawerPaperClose: {
		overflowX: "hidden",
		transition: theme.transitions.create("width", {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		width: theme.spacing(7),
		[theme.breakpoints.up("sm")]: {
			width: theme.spacing(9),
		},
	},
	appBarSpacer: theme.mixins.toolbar,
	content: {
		flexGrow: 1,
		height: "100vh",
		overflow: "auto",
		backgroundColor: "#fafafa",
		marginLeft: theme.spacing(7),
		width: `calc(100% - ${theme.spacing(7)}px)`,
		position: "relative",
		transition: theme.transitions.create(["margin", "width"], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		[theme.breakpoints.up("sm")]: {
			marginLeft: theme.spacing(9),
			width: `calc(100% - ${theme.spacing(9)}px)`,
		},
	},
	contentShift: {
		marginLeft: drawerWidth,
		width: `calc(100% - ${drawerWidth}px)`,
		transition: theme.transitions.create(["margin", "width"], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
	},
	container: {
		marginTop: theme.spacing(4),
		marginBottom: theme.spacing(4),
	},
	paper: {
		padding: theme.spacing(2),
		display: "flex",
		overflow: "auto",
		flexDirection: "column",
	},
	LogoutPaper: {
		padding: theme.spacing(2),
		display: "flex",
		overflow: "auto",
		flexDirection: "column",
		backgroundColor: "white",
	},
	fixedHeight: {
		height: 240,
	},
	margin: {
		margin: theme.spacing(4),
	},
}));
